<template>
  <div class="jpw-form">
    <div v-if="formSubmitted" class="text-center p-4">
      <div class="mb-4 text-success">
        <i class="fas fa-check-circle" style="font-size: 3rem"></i>
      </div>
      <h3 class="mb-3">Thank You!</h3>
      <p class="mb-4">
        We've received your request and will be in touch shortly to arrange a
        meeting at Japan Power Week.
      </p>
      <button @click="resetForm" class="ct-btn-primary">Close</button>
    </div>

    <div v-else-if="formRejected" class="text-center p-4">
      <div class="mb-4 text-warning">
        <i class="fas fa-exclamation-triangle" style="font-size: 3rem"></i>
      </div>
      <h3 class="mb-3">Please use your work email</h3>
      <p class="mb-4">
        We cannot accept personal or free email addresses for demo requests.
      </p>
      <button @click="resetForm" class="ct-btn-primary">Try Again</button>
    </div>

    <form v-else @submit.prevent="submitRequest" class="text-start">
      <!-- Areas of Interest -->
      <div class="mb-4">
        <label class="form-label fw-bold mb-3"
          >What are you interested in?</label
        >

        <!-- Category Cards -->
        <div class="category-cards">
          <div
            v-for="category in categories"
            :key="category.id"
            class="category-card"
            :class="{ expanded: category.expanded }"
          >
            <!-- Category Header -->
            <div class="category-header" @click="toggleCategory(category.id)">
              <div class="category-title">
                <div class="category-icon-placeholder"></div>
                <span>{{ category.label }}</span>
                <span
                  v-if="getSelectedCount(category) > 0"
                  class="selection-count"
                >
                  ({{ getSelectedCount(category) }})
                </span>
              </div>
              <i
                class="fas"
                :class="
                  category.expanded ? 'fa-chevron-down' : 'fa-chevron-right'
                "
              ></i>
            </div>

            <!-- Selection Summary (shown when collapsed and has selections) -->
            <transition name="fade">
              <div
                v-if="!category.expanded && getSelectedCount(category) > 0"
                class="selection-summary"
              >
                {{ getSelectedSummary(category) }}
              </div>
            </transition>

            <!-- Subcategories (shown when expanded) -->
            <transition name="expand">
              <div v-if="category.expanded" class="subcategories">
                <div
                  v-for="sub in category.subcategories"
                  :key="sub.id"
                  class="subcategory-item"
                >
                  <label class="subcategory-label">
                    <input
                      type="checkbox"
                      v-model="sub.selected"
                      class="subcategory-checkbox"
                    />
                    <span class="checkbox-custom"></span>
                    <span class="subcategory-text">{{ sub.label }}</span>
                  </label>

                  <!-- Other text field -->
                  <div
                    v-if="sub.id === 'other' && sub.selected"
                    class="other-input-container"
                  >
                    <input
                      type="text"
                      v-model="category.otherText"
                      class="form-control ct-input"
                      placeholder="Please specify..."
                      required
                    />
                  </div>
                </div>
              </div>
            </transition>
          </div>
        </div>

        <div v-if="errors.interests" class="text-danger small mt-2">
          {{ errors.interests }}
        </div>
      </div>

      <!-- Contact Details -->
      <div class="contact-section">
        <h5 class="mb-3">Your Contact Details</h5>

        <div class="mb-3">
          <label for="name" class="form-label">Name</label>
          <input
            type="text"
            class="form-control ct-input"
            id="name"
            v-model="form.name"
            required
            :class="{ 'is-invalid': errors.name }"
          />
          <div v-if="errors.name" class="invalid-feedback">
            {{ errors.name }}
          </div>
        </div>

        <div class="mb-3">
          <label for="company" class="form-label">Company</label>
          <input
            type="text"
            class="form-control ct-input"
            id="company"
            v-model="form.company"
            required
            :class="{ 'is-invalid': errors.company }"
          />
          <div v-if="errors.company" class="invalid-feedback">
            {{ errors.company }}
          </div>
        </div>

        <div class="mb-3">
          <label for="email" class="form-label">Email</label>
          <input
            type="email"
            class="form-control ct-input"
            id="email"
            v-model="form.email"
            required
            :class="{ 'is-invalid': errors.email }"
          />
          <div v-if="errors.email" class="invalid-feedback">
            {{ errors.email }}
          </div>
        </div>

        <div class="mb-3">
          <label for="phone" class="form-label"
            >Phone <span class="text-muted">(optional)</span></label
          >
          <input
            type="tel"
            class="form-control ct-input"
            id="phone"
            v-model="form.phone"
            :class="{ 'is-invalid': errors.phone }"
          />
          <div v-if="errors.phone" class="invalid-feedback">
            {{ errors.phone }}
          </div>
        </div>

        <div class="mb-3">
          <label for="message" class="form-label"
            >Additional Information
            <span class="text-muted">(optional)</span></label
          >
          <textarea
            class="form-control ct-input"
            id="message"
            rows="3"
            v-model="form.message"
            :class="{ 'is-invalid': errors.message }"
          ></textarea>
          <div v-if="errors.message" class="invalid-feedback">
            {{ errors.message }}
          </div>
        </div>
      </div>

      <div v-if="submissionError" class="alert alert-danger">
        {{ submissionError }}
      </div>

      <div class="text-center">
        <button type="submit" class="ct-btn-primary" :disabled="submitting">
          <span v-if="submitting">
            <i class="fas fa-spinner fa-spin me-2"></i>Sending...
          </span>
          <span v-else>Request Meeting</span>
        </button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref, reactive } from "vue";
import apiClient from "@/services/api.js";

const categories = reactive([
  {
    id: "chatToScreen",
    label: "Chat to Screen",
    expanded: false,
    subcategories: [
      { id: "whatsapp", label: "WhatsApp", selected: false },
      { id: "lsegMessenger", label: "LSEG Messenger", selected: false },
      { id: "iceChat", label: "ICE Chat", selected: false },
      { id: "other", label: "Other", selected: false },
    ],
    otherText: "",
  },
  {
    id: "jepx",
    label: "JEPX",
    expanded: false,
    subcategories: [
      { id: "withinDay", label: "Within Day", selected: false },
      { id: "dayAhead", label: "Day Ahead", selected: false },
      { id: "other", label: "Other", selected: false },
    ],
    otherText: "",
  },
  {
    id: "tocomBlockTrades",
    label: "TOCOM Block Trades",
    expanded: false,
    subcategories: [
      { id: "mufg", label: "MUFG", selected: false },
      { id: "okachi", label: "Okachi", selected: false },
      { id: "nissan", label: "Nissan", selected: false },
      { id: "phillip", label: "Phillip", selected: false },
      { id: "yutaka", label: "Yutaka", selected: false },
      { id: "other", label: "Other", selected: false },
    ],
    otherText: "",
  },
]);

const form = reactive({
  name: "",
  company: "",
  email: "",
  phone: "",
  message: "",
});

const errors = reactive({
  name: "",
  company: "",
  email: "",
  phone: "",
  message: "",
  interests: "",
});

const submitting = ref(false);
const formSubmitted = ref(false);
const formRejected = ref(false);
const submissionError = ref("");

const toggleCategory = (categoryId) => {
  const category = categories.find((c) => c.id === categoryId);
  if (category) {
    const wasExpanded = category.expanded;

    // If clicking an already expanded category, just close it
    if (wasExpanded) {
      category.expanded = false;
    } else {
      // First close all categories
      categories.forEach((cat) => {
        if (cat.id !== categoryId) {
          cat.expanded = false;
        }
      });

      // Then open the clicked category after delay
      // Delay should be roughly half the transition time (150ms of 300ms)
      setTimeout(() => {
        category.expanded = true;
      }, 150);
    }
  }
};

const getSelectedCount = (category) => {
  return category.subcategories.filter((sub) => sub.selected).length;
};

const getSelectedSummary = (category) => {
  const selectedSubs = category.subcategories.filter((sub) => sub.selected);

  const labels = selectedSubs.map((sub) => {
    if (sub.id === "other" && category.otherText) {
      return `Other: ${category.otherText}`;
    }
    return sub.label;
  });

  return labels.join(", ");
};

const resetForm = () => {
  form.name = "";
  form.company = "";
  form.email = "";
  form.phone = "";
  form.message = "";

  categories.forEach((cat) => {
    cat.expanded = false;
    cat.subcategories.forEach((sub) => {
      sub.selected = false;
    });
    if (cat.otherText !== undefined) {
      cat.otherText = "";
    }
  });

  errors.name = "";
  errors.company = "";
  errors.email = "";
  errors.phone = "";
  errors.message = "";
  errors.interests = "";

  formRejected.value = false;
  formSubmitted.value = false;
  submissionError.value = "";
};

const formatInterests = () => {
  const lines = ["Areas of Interest:\n"];

  categories.forEach((category) => {
    const selectedSubs = category.subcategories.filter((sub) => sub.selected);

    if (selectedSubs.length > 0) {
      lines.push(`\n${category.label}:`);

      selectedSubs.forEach((sub) => {
        if (sub.id === "other" && category.otherText) {
          lines.push(`  • ${sub.label}: "${category.otherText}"`);
        } else {
          lines.push(`  • ${sub.label}`);
        }
      });
    }
  });

  return lines.join("\n");
};

const validateForm = () => {
  let isValid = true;

  // Reset errors
  errors.name = "";
  errors.company = "";
  errors.email = "";
  errors.phone = "";
  errors.message = "";
  errors.interests = "";

  // Check at least one subcategory is selected
  const hasSelection = categories.some((cat) =>
    cat.subcategories.some((sub) => sub.selected)
  );

  if (!hasSelection) {
    errors.interests = "Please select at least one area of interest";
    isValid = false;
  }

  // Validate other text fields if "Other" is selected
  categories.forEach((cat) => {
    const otherSub = cat.subcategories.find((sub) => sub.id === "other");
    if (
      otherSub &&
      otherSub.selected &&
      cat.otherText !== undefined &&
      !cat.otherText.trim()
    ) {
      errors.interests = 'Please specify "Other" option';
      isValid = false;
    }
  });

  if (!form.name.trim()) {
    errors.name = "Name is required";
    isValid = false;
  }

  if (!form.company.trim()) {
    errors.company = "Company is required";
    isValid = false;
  }

  if (!form.email.trim()) {
    errors.email = "Email is required";
    isValid = false;
  } else if (!/^\S+@\S+\.\S+$/.test(form.email)) {
    errors.email = "Please enter a valid email address";
    isValid = false;
  }

  return isValid;
};

const submitRequest = () => {
  if (!validateForm()) return;

  submitting.value = true;
  submissionError.value = "";
  formRejected.value = false;

  const interestsText = formatInterests();

  // Create request payload
  const payload = {
    email: form.email,
    telephone: form.phone || "",
    name: form.name || "",
    company: form.company || "",
    message: `${interestsText}\n\n${form.message || ""}`.trim(),
    reason: "Japan Power Week 2025",
    campaign: "Japan Power Week 2025",
  };

  // Make API request
  apiClient
    .post("/contact/", payload)
    .then(() => {
      submitting.value = false;
      formSubmitted.value = true;
    })
    .catch((error) => {
      submitting.value = false;

      if (error.response && error.response.status === 422) {
        formRejected.value = true;
      } else {
        submissionError.value =
          error.response?.data?.error ||
          error.message ||
          "There was an error submitting your request. Please try again.";
      }
    });
};
</script>

<style scoped>
.jpw-form {
  width: 100%;
}

/* Category Cards */
.category-cards {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.category-card {
  background-color: var(--ct-dark-secondary);
  border: 2px solid var(--ct-dark-tertiary);
  border-radius: 12px;
  overflow: visible;
  transition: all 0.3s ease;
  position: relative;
}

.category-card:hover {
  border-color: var(--ct-orange);
}

.category-card.expanded {
  border-color: var(--ct-orange);
  border-left-width: 4px;
}

/* Category Header */
.category-header {
  padding: 18px 20px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  user-select: none;
  min-height: 48px;
}

.category-title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 600;
  font-size: 1.05rem;
}

.selection-count {
  color: var(--ct-orange);
  font-weight: 500;
  font-size: 0.95rem;
  margin-left: 4px;
}

.selection-summary {
  padding: 0 20px 16px 68px; /* Align with title text (icon + gap) */
  color: var(--ct-text-muted);
  font-size: 0.9rem;
  line-height: 1.4;
  font-weight: 400;
  opacity: 0.7;
}

.category-icon-placeholder {
  width: 36px;
  height: 36px;
  background: linear-gradient(
    135deg,
    var(--ct-orange) 0%,
    var(--ct-orange-dark) 100%
  );
  border-radius: 8px;
  flex-shrink: 0;
}

.category-header i {
  color: var(--ct-orange);
  transition: transform 0.3s ease;
}

/* Subcategories */
.subcategories {
  padding: 0 20px 20px 20px;
  background-color: rgba(0, 0, 0, 0.2);
  position: relative;
}

.subcategory-item {
  padding: 12px 0;
  border-top: 1px solid var(--ct-dark-tertiary);
}

.subcategory-item:first-child {
  border-top: none;
  padding-top: 16px;
}

.subcategory-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  position: relative;
  padding-left: 40px;
  min-height: 32px;
  user-select: none;
}

.subcategory-checkbox {
  position: absolute;
  opacity: 0;
  cursor: pointer;
}

.checkbox-custom {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  border: 2px solid var(--ct-dark-tertiary);
  border-radius: 6px;
  background-color: var(--ct-dark);
  transition: all 0.3s ease;
}

.subcategory-checkbox:checked ~ .checkbox-custom {
  background-color: var(--ct-orange);
  border-color: var(--ct-orange);
}

.subcategory-checkbox:checked ~ .checkbox-custom::after {
  content: "";
  position: absolute;
  left: 7px;
  top: 3px;
  width: 6px;
  height: 12px;
  border: solid #000;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.subcategory-text {
  font-size: 0.95rem;
}

.subcategory-label:hover .checkbox-custom {
  border-color: var(--ct-orange);
}

/* Other Input */
.other-input-container {
  margin-top: 12px;
  margin-left: 40px;
  animation: slideDown 0.3s ease;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Fade Transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Expand Transition */
.expand-enter-active,
.expand-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
  overflow: hidden;
  will-change: opacity, transform;
  transform-origin: top;
  backface-visibility: hidden;
}

.expand-leave-active {
  position: absolute;
  width: 100%;
  left: 0;
}

.expand-enter-from {
  opacity: 0;
  transform: scaleY(0.95) translateY(-10px);
}

.expand-enter-to {
  opacity: 1;
  transform: scaleY(1) translateY(0);
}

.expand-leave-from {
  opacity: 1;
  transform: scaleY(1) translateY(0);
}

.expand-leave-to {
  opacity: 0;
  transform: scaleY(0.95) translateY(-10px);
}

/* Contact Section */
.contact-section {
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 2px solid var(--ct-dark-tertiary);
  /* Enable GPU acceleration for smooth movement during accordion transitions */
  transform: translateZ(0);
  will-change: transform;
  transition: transform 0.3s ease;
}

.invalid-feedback {
  display: block;
}

/* Responsive */
@media (max-width: 768px) {
  .category-header {
    padding: 16px;
  }

  .subcategories {
    padding: 0 16px 16px 16px;
  }

  .category-icon-placeholder {
    width: 32px;
    height: 32px;
  }
}
</style>
